/**
 * ThemeModeProvider (ported from dink-admin DNKADM-0266).
 *
 * Persists the user's mode preference under the storage key
 * `tmm_reseller_admin.themeMode` and resolves `system` via
 * `matchMedia('(prefers-color-scheme: dark)')`, with a live listener so the
 * app follows an OS theme change. Both storage and matchMedia access are
 * try/catch-guarded: private mode (storage throws) falls back to the `system`
 * default without persisting; no `matchMedia` (older engines, bare jsdom)
 * resolves to `light`.
 */
import { useState, useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import { ThemeModeContext, type ThemeMode, type ThemeModeContextValue } from './theme-mode-context';

const STORAGE_KEY = 'tmm_reseller_admin.themeMode';

function readStoredMode(): ThemeMode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    /* localStorage unavailable (e.g. private mode) — fall through to default */
  }
  return 'system';
}

function getSystemMode(): 'light' | 'dark' {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch {
    /* matchMedia unavailable — fall through to light */
  }
  return 'light';
}

export function ThemeModeProvider({ children }: { children: ReactNode }) {
  const [themeMode, setThemeModeState] = useState<ThemeMode>(readStoredMode);
  const [systemMode, setSystemMode] = useState<'light' | 'dark'>(getSystemMode);

  useEffect(() => {
    try {
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      const handler = (e: MediaQueryListEvent) => setSystemMode(e.matches ? 'dark' : 'light');
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    } catch {
      /* matchMedia unavailable — no system-theme listener */
    }
  }, []);

  function setThemeMode(mode: ThemeMode) {
    setThemeModeState(mode);
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      /* localStorage unavailable — preference not persisted */
    }
  }

  const resolvedMode: 'light' | 'dark' = themeMode === 'system' ? systemMode : themeMode;

  const value = useMemo<ThemeModeContextValue>(
    () => ({ themeMode, resolvedMode, setThemeMode }),
    [themeMode, resolvedMode],
  );

  return <ThemeModeContext.Provider value={value}>{children}</ThemeModeContext.Provider>;
}
